/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { BnLandGeoJson } from '@poweralpha/common';
import {
  divIcon,
  FeatureGroup,
  geoJSON,
  type Map as LeafletMap,
  marker,
  type PathOptions,
} from 'leaflet';
import { useEffect } from 'react';
import { normalizeSiteId, siteDefaultStyle, siteMouseoverStyle } from './utils';

export const SITE_LABEL_MIN_ZOOM = 14;
const VERIFIED_SITE_NAME = /^SITE-\d{8}$/i;

interface SiteBoundaryLayerOptions {
  json: BnLandGeoJson;
  style?: (siteId: string) => PathOptions;
  label?: (siteId: string) => string | undefined;
}

export interface SiteBoundaryLayers {
  boundaries: FeatureGroup;
  labels: FeatureGroup;
}

export const verifiedSiteLabel = (siteId: string): string | undefined =>
  VERIFIED_SITE_NAME.test(siteId) ? siteId.toUpperCase() : undefined;

export const createSiteBoundaryLayers = ({
  json,
  style,
  label = verifiedSiteLabel,
}: SiteBoundaryLayerOptions): SiteBoundaryLayers => {
  const boundaries = new FeatureGroup();
  const labels = new FeatureGroup();
  const siteIds = Array.from(
    new Set(
      json.features
        .map((item) => normalizeSiteId(item.properties.BN_CRM_Site_ID))
        .filter((siteId): siteId is string => Boolean(siteId)),
    ),
  );

  siteIds.forEach((siteId) => {
    const defaultStyle = style?.(siteId) ?? siteDefaultStyle;
    const mouseoverStyle = { ...defaultStyle, weight: siteMouseoverStyle.weight };
    const siteLayer = geoJSON(json, {
      pane: 'projectSitePane',
      filter: (parcel) => normalizeSiteId(parcel.properties.BN_CRM_Site_ID) === siteId,
      style: defaultStyle,
    });
    boundaries.addLayer(siteLayer);

    const labelText = label(siteId);
    const bounds = siteLayer.getBounds();
    const labelMarker =
      labelText && bounds.isValid()
        ? marker(bounds.getCenter(), {
            interactive: false,
            keyboard: false,
            pane: 'projectSiteLabelPane',
            icon: divIcon({
              className: 'project-site-name-label',
              html: `<span>${escapeHtml(labelText)}</span>`,
              iconSize: undefined,
            }),
          })
        : undefined;
    if (labelMarker) labels.addLayer(labelMarker);

    const setHovered = (hovered: boolean) => {
      siteLayer.setStyle(hovered ? mouseoverStyle : defaultStyle);
      labelMarker?.getElement()?.classList.toggle('project-site-name-label--active', hovered);
      labelMarker?.setZIndexOffset(hovered ? 1000 : 0);
    };
    siteLayer.on('mouseover', () => setHovered(true)).on('mouseout', () => setHovered(false));
  });

  return { boundaries, labels };
};

const escapeHtml = (value: string): string =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' })[character] ??
      character,
  );

export const useSiteLabelLayer = (
  map: LeafletMap,
  labels: FeatureGroup | null | undefined,
  enabled: boolean,
  minZoom = SITE_LABEL_MIN_ZOOM,
): void => {
  useEffect(() => {
    if (!labels) return;
    const updateVisibility = () => {
      if (enabled && map.getZoom() >= minZoom) {
        labels.addTo(map);
      } else {
        labels.remove();
      }
    };

    updateVisibility();
    map.on('zoomend', updateVisibility);
    return () => {
      map.off('zoomend', updateVisibility);
      labels.remove();
    };
  }, [enabled, labels, map, minZoom]);
};
